/**
 * Independent recovery probe while unreachable — breaks RQ self-latch.
 * Uses raw fetch (not supabase client) to avoid module cycles.
 */

import { recordFetchOutcome } from './fetchOutcomes';
import {
  EFFECTIVE_CONNECTIVITY,
  getEffectiveConnectivity,
  isForceUnreachable,
  setForceUnreachable,
  subscribeEffectiveConnectivityInputs,
} from './effectiveConnectivity';

const PROBE_MIN_MS = 5_000;
const PROBE_MAX_MS = 60_000;
const PROBE_TIMEOUT_MS = 5_000;

let probeTimer = null;
let backoffMs = PROBE_MIN_MS;
let unsubscribe = null;
let started = false;

function getProbeUrl() {
  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) return null;
  return `${String(base).replace(/\/$/, '')}/auth/v1/health`;
}

function getProbeHeaders() {
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!apikey) return {};
  return { apikey };
}

function clearProbeTimer() {
  if (probeTimer != null) {
    clearTimeout(probeTimer);
    probeTimer = null;
  }
}

/** Schedule the next probe tick — independent of in-flight attempt completion. */
function scheduleProbe(delayMs) {
  clearProbeTimer();
  const jitter = Math.random() * 1_000;
  probeTimer = setTimeout(probeTick, delayMs + jitter);
}

function probeTick() {
  probeTimer = null;

  if (getEffectiveConnectivity() !== EFFECTIVE_CONNECTIVITY.UNREACHABLE) {
    backoffMs = PROBE_MIN_MS;
    return;
  }

  if (isForceUnreachable()) {
    return;
  }

  // Queue the next attempt before starting this one so a hung fetch cannot stall recovery.
  scheduleProbe(backoffMs);
  void executeProbeAttempt();
}

async function executeProbeAttempt() {
  if (getEffectiveConnectivity() !== EFFECTIVE_CONNECTIVITY.UNREACHABLE) {
    return;
  }

  const url = getProbeUrl();
  if (!url) {
    backoffMs = Math.min(PROBE_MAX_MS, Math.round(backoffMs * 1.6));
    return;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
      headers: getProbeHeaders(),
    });

    if (response.ok) {
      recordFetchOutcome({ ok: true });
      backoffMs = PROBE_MIN_MS;
      if (import.meta.env.DEV) {
        console.log('[connectivity] recovery probe succeeded');
      }
      return;
    }

    backoffMs = Math.min(PROBE_MAX_MS, Math.round(backoffMs * 1.6));
    if (import.meta.env.DEV) {
      console.warn('[connectivity] recovery probe HTTP', response.status);
    }
  } catch (err) {
    backoffMs = Math.min(PROBE_MAX_MS, Math.round(backoffMs * 1.6));
    if (import.meta.env.DEV) {
      const timedOut = err?.name === 'AbortError';
      console.warn('[connectivity] recovery probe failed:', timedOut ? 'timeout' : err);
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

function syncProbeToConnectivityState() {
  const state = getEffectiveConnectivity();

  if (state === EFFECTIVE_CONNECTIVITY.UNREACHABLE && !isForceUnreachable()) {
    if (!probeTimer) {
      backoffMs = PROBE_MIN_MS;
      scheduleProbe(0);
    }
    return;
  }

  clearProbeTimer();
  backoffMs = PROBE_MIN_MS;
}

/** Start listening — probe runs on exponential backoff while unreachable. */
export function initConnectivityRecoveryProbe() {
  if (started || typeof window === 'undefined') return;
  started = true;

  unsubscribe = subscribeEffectiveConnectivityInputs(syncProbeToConnectivityState);
  syncProbeToConnectivityState();
}

export function stopConnectivityRecoveryProbe() {
  clearProbeTimer();
  unsubscribe?.();
  unsubscribe = null;
  started = false;
  backoffMs = PROBE_MIN_MS;
}

/** Manual probe trigger for dev/testing. */
export async function runConnectivityProbeNow() {
  await executeProbeAttempt();
}

function installDevConnectivityTools() {
  if (!import.meta.env.DEV || typeof window === 'undefined') return;
  window.__priiConnectivity = {
    getState: () => getEffectiveConnectivity(),
    forceUnreachable: (value = true) => setForceUnreachable(Boolean(value)),
    clearForce: () => setForceUnreachable(false),
    runProbe: () => runConnectivityProbeNow(),
  };
}

installDevConnectivityTools();
